import { createContext, useContext } from 'react'
import type { LumpsumAllocationPlansResponse } from '../api/allocation.types'
import type { LumpsumIdealPlansResponse } from '../api/ideal.types'
import type { LumpsumRebalancePlansResponse } from '../api/rebalance.types'
import {
  AllocationMovementToggle,
  PlansActionFilter,
  PlansPlanMode
} from '../api/types'
import type { PlanCategoryExpansionState } from './usePlanCategoryBucketExpansion'

export type LumpsumPlansQueryState = {
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
}

export type LumpsumPlansContextValue = {
  planMode: PlansPlanMode
  handlePlanModeChange: (planMode: PlansPlanMode) => void
  actionFilter: PlansActionFilter
  actionFiltersByPlanMode: Record<PlansPlanMode, PlansActionFilter>
  handleActionFilterChange: (filter: PlansActionFilter) => void
  allocationMovementToggle: AllocationMovementToggle
  handleAllocationMovementToggleChange: (toggle: AllocationMovementToggle) => void
  rebalancePlansData: LumpsumRebalancePlansResponse | undefined
  allocationPlansData: LumpsumAllocationPlansResponse | undefined
  idealData: LumpsumIdealPlansResponse | undefined
  rebalancePlansQuery: LumpsumPlansQueryState
  allocationPlansQuery: LumpsumPlansQueryState
  idealPlansQuery: LumpsumPlansQueryState
  /** Refetch allocation, rebalance, and ideal (e.g. strategy timer resume). */
  refetchAllPlansQueries: () => void
  /** Mark that strategy timer resume refetch ran (for leave). */
  markTimerResumeRefetch: () => void
  leavePlansList: () => void
} & PlanCategoryExpansionState

export const LumpsumPlansContext = createContext<LumpsumPlansContextValue | null>(null)

export function useLumpsumPlansContext(): LumpsumPlansContextValue {
  const contextValue = useContext(LumpsumPlansContext)
  if (!contextValue) {
    throw new Error('useLumpsumPlansContext must be used within LumpsumPlansProvider')
  }
  return contextValue
}
